import { AlertTriangle, CheckCircle2, Clock3, ShieldOff } from 'lucide-react';

export interface CredentialLifecycle {
  disabledAt?: string | null;
  lastUsedAt?: string | null;
  lastSuccessAt?: string | null;
  lastErrorAt?: string | null;
  lastErrorStatus?: number | null;
  lastErrorMessage?: string | null;
  consecutiveAuthFailures?: number | null;
  quarantineCount?: number | null;
  quarantinedUntil?: string | null;
}

export type CredentialStateKind = 'active' | 'warning' | 'quarantined' | 'disabled';

export function getCredentialState(credential: CredentialLifecycle): {
  kind: CredentialStateKind;
  label: string;
  detail: string;
} {
  if (credential.disabledAt) {
    return {
      kind: 'disabled',
      label: 'Disabled',
      detail: `Disabled ${formatRelativeTime(credential.disabledAt)}`,
    };
  }

  const quarantinedUntil = credential.quarantinedUntil;
  if (quarantinedUntil && isFuture(quarantinedUntil)) {
    return {
      kind: 'quarantined',
      label: 'Quarantined',
      detail: `Second chance ${formatRelativeTime(quarantinedUntil)}`,
    };
  }

  const failures = Number(credential.consecutiveAuthFailures ?? 0);
  const lastErrorIsCurrent =
    !!credential.lastErrorAt &&
    (!credential.lastSuccessAt || Date.parse(credential.lastErrorAt) > Date.parse(credential.lastSuccessAt));
  if (failures > 0 || lastErrorIsCurrent) {
    return {
      kind: 'warning',
      label: 'Warning',
      detail: failures > 0 ? `${failures} auth failure${failures === 1 ? '' : 's'}` : 'Recent error',
    };
  }

  return {
    kind: 'active',
    label: 'Active',
    detail: credential.lastSuccessAt
      ? `Last success ${formatRelativeTime(credential.lastSuccessAt)}`
      : 'Ready',
  };
}

export function isCredentialEnabled(credential: CredentialLifecycle): boolean {
  return !credential.disabledAt;
}

export function CredentialStateBadge({ credential }: { credential: CredentialLifecycle }) {
  const state = getCredentialState(credential);
  const Icon =
    state.kind === 'active' ? CheckCircle2 :
    state.kind === 'warning' ? AlertTriangle :
    state.kind === 'quarantined' ? Clock3 :
    ShieldOff;
  const classes =
    state.kind === 'active' ? 'bg-emerald-500/10 text-emerald-500 dark:text-emerald-400' :
    state.kind === 'warning' ? 'bg-amber-500/10 text-amber-500 dark:text-amber-400' :
    state.kind === 'quarantined' ? 'bg-blue-500/10 text-blue-500 dark:text-blue-400' :
    'bg-muted text-muted-foreground';

  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${classes}`}>
      <Icon size={11} />
      {state.label}
    </span>
  );
}

/**
 * Credential health lines. Default: the original self-contained stacked block (one line
 * per item). `inline`: a compact fragment for the org key panels that flows into a
 * flex-wrap meta row — healthy items (status · used · quarantines) inline, the long
 * error message dropping to its own basis-full line. The fragment MUST live inside a
 * flex-wrap parent; the default block can live anywhere.
 */
export function CredentialHealthLines({
  credential,
  inline = false,
}: {
  credential: CredentialLifecycle;
  inline?: boolean;
}) {
  const state = getCredentialState(credential);

  if (inline) {
    const items = [
      state.detail,
      credential.lastUsedAt ? `Used ${formatRelativeTime(credential.lastUsedAt)}` : null,
      Number(credential.quarantineCount ?? 0) > 0
        ? `${credential.quarantineCount} quarantine${credential.quarantineCount === 1 ? '' : 's'}`
        : null,
    ].filter(Boolean) as string[];
    const lastError = credential.lastErrorAt
      ? `Last error ${formatRelativeTime(credential.lastErrorAt)}${credential.lastErrorStatus ? ` (${credential.lastErrorStatus})` : ''}`
      : null;
    const errorMessage = credential.lastErrorMessage ? truncate(credential.lastErrorMessage, 140) : null;
    return (
      <>
        {items.map((line) => (
          <span key={line} className="inline-flex items-center gap-2">
            <span className="text-muted-foreground/40">·</span>
            {line}
          </span>
        ))}
        {(lastError || errorMessage) && (
          <span className="text-muted-foreground/70 flex basis-full flex-wrap items-center gap-x-2 gap-y-0.5">
            {lastError && <span>{lastError}</span>}
            {lastError && errorMessage && <span className="text-muted-foreground/40">·</span>}
            {errorMessage && <span className="font-mono text-[10px]">{errorMessage}</span>}
          </span>
        )}
      </>
    );
  }

  const lines = [
    state.detail,
    credential.lastUsedAt ? `Used ${formatRelativeTime(credential.lastUsedAt)}` : null,
    credential.lastErrorAt
      ? `Last error ${formatRelativeTime(credential.lastErrorAt)}${credential.lastErrorStatus ? ` (${credential.lastErrorStatus})` : ''}`
      : null,
    credential.lastErrorMessage ? truncate(credential.lastErrorMessage, 96) : null,
    Number(credential.quarantineCount ?? 0) > 0
      ? `${credential.quarantineCount} quarantine${credential.quarantineCount === 1 ? '' : 's'}`
      : null,
  ].filter(Boolean);

  return (
    <div className="text-muted-foreground space-y-0.5 text-[11px] leading-snug">
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
}

function isFuture(value?: string | null): boolean {
  if (!value) return false;
  const ms = Date.parse(value);
  return Number.isFinite(ms) && ms > Date.now();
}

function formatRelativeTime(value: string): string {
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return 'unknown';
  const delta = ms - Date.now();
  const abs = Math.abs(delta);
  const unit =
    abs < 60_000 ? 'second' :
    abs < 3_600_000 ? 'minute' :
    abs < 86_400_000 ? 'hour' :
    'day';
  const divisor =
    unit === 'second' ? 1_000 :
    unit === 'minute' ? 60_000 :
    unit === 'hour' ? 3_600_000 :
    86_400_000;
  const valueForUnit = Math.max(1, Math.round(abs / divisor));
  const suffix = valueForUnit === 1 ? unit : `${unit}s`;
  return delta > 0 ? `in ${valueForUnit} ${suffix}` : `${valueForUnit} ${suffix} ago`;
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}...`;
}
